import { useState } from 'react'
import { PiX } from 'react-icons/pi'

import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { DsInput, DsTextArea, FieldLabel } from 'components/FbUI/Ds2026Field'
import { Alert } from 'components/FbUI/Alert'

import { useSaveSequenceAsTemplate } from './sequence_templates_api'

/**
 * Save-as-template modal (FIR-7633) — DS2026 modal styling. Content-scoped
 * (absolute) so it dims the build step only, not the global nav.
 */
interface Props {
  open: boolean
  onClose: () => void
  campaignId: number
  stepCount: number
  /** Called after a successful save (e.g. to toast / open the drawer). */
  onSaved?: () => void
}

export function SaveSequenceTemplateModal({
  open,
  onClose,
  campaignId,
  stepCount,
  onSaved,
}: Props) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const saveMutation = useSaveSequenceAsTemplate()

  if (!open) return null

  const reset = () => {
    setName('')
    setDescription('')
    saveMutation.reset()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSave = () => {
    if (!name.trim()) return
    saveMutation.mutate(
      {
        campaign_id: campaignId,
        name: name.trim(),
        description: description.trim(),
      },
      {
        onSuccess: () => {
          reset()
          onSaved?.()
          onClose()
        },
      }
    )
  }

  return (
    <Ds2026ButtonProvider>
      <div className="absolute inset-0 z-30 flex items-center justify-center">
        <button
          type="button"
          aria-label="Cancel"
          className="absolute inset-0 h-full w-full bg-black/40"
          onClick={handleClose}
        />
        <div className="relative w-[440px] max-w-[92vw] rounded-2xl border border-line bg-surface px-10 py-6 shadow-xl">
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500"
          >
            <PiX className="h-5 w-5" />
          </button>

          <h2 className="text-lg font-semibold tracking-[-0.2px] text-neutral-800">
            Save as template
          </h2>
          <p className="mb-3 mt-1 text-sm text-neutral-500">
            Saves the current {stepCount}-step sequence so you can reuse its
            copy and delays on a new campaign.
          </p>

          <div className="flex w-full flex-col gap-1.5">
            <FieldLabel htmlFor="template-name">Template name</FieldLabel>
            <DsInput
              id="template-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Bakery outreach — West Coast"
            />
          </div>

          <div className="mt-4 flex w-full flex-col gap-1.5">
            <FieldLabel htmlFor="template-description" optional>
              Description
            </FieldLabel>
            <DsTextArea
              id="template-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this template for? (e.g. territory-split bakery outreach)"
            />
          </div>

          {saveMutation.isError && (
            <div className="mt-4">
              <Alert
                color="danger"
                description="Couldn't save the template. Please try again."
              />
            </div>
          )}

          <div className="mt-8 flex gap-[16px]">
            <FbButton
              variant="secondary"
              onClick={handleClose}
              className="flex-1 !h-[44px] !min-h-[44px]"
            >
              Cancel
            </FbButton>
            <FbButton
              variant="primary"
              loading={saveMutation.isPending}
              disabled={!name.trim()}
              onClick={handleSave}
              className="flex-1 !h-[44px] !min-h-[44px]"
            >
              Save template
            </FbButton>
          </div>
        </div>
      </div>
    </Ds2026ButtonProvider>
  )
}
